import { ArrowLeft, Mail, MessageCircle, Bug, Lightbulb } from 'lucide-react'

export default function ContactPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-3 sm:py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <img 
                src="/images/sportspot-transparent-bg.png" 
                alt="Sportspot Logo" 
                className="h-8 sm:h-12 w-auto"
              />
            </div>
            <div>
              <a href="/" className="inline-flex items-center text-gray-600 hover:text-gray-900 transition-colors">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Home
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold text-gray-900 mb-8">Contact Us</h1>
          
          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <h2 className="text-2xl font-semibold text-gray-900 mb-6">Get in Touch</h2>
              <p className="text-gray-700 mb-8">
                We'd love to hear from you! Whether you have questions, feedback, or need support, our team is here to help make your Sportspot experience amazing.
              </p>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <Mail className="h-6 w-6 text-[#42a5f5] mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Email Support</h3>
                    <p className="text-gray-700">support@sportspot.com</p>
                    <p className="text-sm text-gray-500">We typically respond within 24 hours</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <MessageCircle className="h-6 w-6 text-[#42a5f5] mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900">General Inquiries</h3>
                    <p className="text-gray-700">hello@sportspot.com</p>
                    <p className="text-sm text-gray-500">For partnerships, media, and general questions</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <Bug className="h-6 w-6 text-[#42a5f5] mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Bug Reports</h3>
                    <p className="text-gray-700">bugs@sportspot.com</p>
                    <p className="text-sm text-gray-500">Help us improve by reporting issues</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <Lightbulb className="h-6 w-6 text-[#42a5f5] mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Feature Requests</h3>
                    <p className="text-gray-700">features@sportspot.com</p>
                    <p className="text-sm text-gray-500">Share your ideas for new features</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <h2 className="text-2xl font-semibold text-gray-900 mb-6">Send us a Message</h2>
              <form className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#42a5f5] focus:border-transparent"
                    placeholder="Your name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#42a5f5] focus:border-transparent"
                    placeholder="your@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                    Subject
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#42a5f5] focus:border-transparent"
                  >
                    <option value="">Select a topic</option>
                    <option value="support">Technical Support</option>
                    <option value="feedback">Feedback</option>
                    <option value="feature">Feature Request</option>
                    <option value="bug">Bug Report</option>
                    <option value="partnership">Partnership</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={6}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#42a5f5] focus:border-transparent"
                    placeholder="Tell us how we can help..."
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="w-full bg-[#42a5f5] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#1976d2] transition-colors"
                >
                  Send Message
                </button>
              </form>
            </div>
          </div>

          {/* Additional Information */}
          <div className="mt-16 bg-gray-50 rounded-lg p-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-gray-700 mb-4">
              Before reaching out, you might find the answer to your question in our comprehensive FAQ section.
            </p>
            <a 
              href="/faq" 
              className="inline-flex items-center text-[#42a5f5] hover:text-[#1976d2] font-medium"
            >
              Visit FAQ →
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}
